import {
  Button,
  Divider,
  HStack,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { stringTaxRates } from "../../../strings";
import TaxRatesTable from "../../../components/tax-rates-table";
import ShowDeletedToggle from "../../../components/show-deleted-toggle";
import { useContextTypeSettingsBookkeeping } from "../dashboard-layout";
import {
  AccountingSystem,
  CreateTaxRateInput,
  UpdateTaxRateInput,
} from "../../../API";
import { FieldArray, Form, Formik } from "formik";
import { TaxRate } from "../../../models";
import { useNavigate } from "react-router-dom";
import { createTaxRate, updateTaxRate } from "../../../graphql/mutations";
import { API, Auth, graphqlOperation } from "aws-amplify";
import { GraphQLQuery } from "@aws-amplify/api";
import {
  AccountingSystemTax,
  TaxRateValue,
} from "../../create-organization/tax-rates";
import { deleteItem, isValidTaxRate } from "../../../utils";
import equal from "fast-deep-equal";
import { useEffect, useMemo, useState } from "react";

type TaxRatesValues = {
  taxRates?: TaxRateValue[];
};

const typeNamePlural = stringTaxRates;

const TaxRates = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [showDeleted, setShowDeleted] = useState(false);

  const { user, selectedOrganization, selectedBookkeepingFull } =
    useContextTypeSettingsBookkeeping();

  const [accountingSystemTaxes, setAccountingSystemTaxes] =
    useState<AccountingSystemTax[]>();

  const hasDeletedItems = useMemo(
    () =>
      selectedBookkeepingFull?.taxRates?.some((taxRate) => taxRate.deletedAt) ??
      false,
    [selectedBookkeepingFull?.taxRates]
  );

  const filteredTaxRates = useMemo(() => {
    if (showDeleted) {
      return selectedBookkeepingFull?.taxRates;
    }
    return selectedBookkeepingFull?.taxRates?.filter(
      (taxRate) => !taxRate.deletedAt
    );
  }, [selectedBookkeepingFull?.taxRates, showDeleted]);

  const hasAccountingSystemTaxes =
    selectedBookkeepingFull?.accountingSystem === AccountingSystem.bexio;

  useEffect(() => {
    if (
      hasAccountingSystemTaxes &&
      !accountingSystemTaxes &&
      selectedOrganization?.id
    ) {
      const fetchTaxRates = async () => {
        try {
          const response: AccountingSystemTax[] = await API.post(
            "expenslyREST",
            "/auth/bexio/proxy",
            {
              headers: {
                Authorization: `Bearer ${(await Auth.currentSession())
                  .getIdToken()
                  .getJwtToken()}`,
              },
              body: {
                organizationId: selectedOrganization.id,
                path: "/3.0/taxes",
                method: "GET",
              },
            }
          );

          const filtered = response.filter((account) => account.is_active);
          filtered.sort((a, b) => a.code?.localeCompare(b.code) ?? 1);

          setAccountingSystemTaxes(filtered);
        } catch (error) {
          console.error(error);
        }
      };
      fetchTaxRates();
    }
  }, [accountingSystemTaxes, hasAccountingSystemTaxes, selectedOrganization?.id]);

  const initialValues: TaxRatesValues = {
    taxRates: filteredTaxRates?.map((tr) => ({
      id: tr.id,
      code: tr.code,
      rate: tr.rate,
      dateFrom: new Date(tr.dateFrom),
      dateTo: tr.dateTo ? new Date(tr.dateTo) : null,
      taxType: tr.taxType,
      accountingSystemId: tr.accountingSystemId ?? undefined,
      deletedAt: tr.deletedAt,
    })),
  };

  const isValid = (values: TaxRatesValues) =>
    !hasAccountingSystemTaxes ||
    (accountingSystemTaxes &&
      values.taxRates?.every((tr) =>
        accountingSystemTaxes.find((ast) => ast.code === tr.code)
      ));

  return (
    <VStack width="full" height="full" alignItems="start" spacing="20px">
      <HStack width="full" justifyContent="space-between" alignItems="start">
        <VStack alignItems="start" spacing="4px">
          <Text fontSize={18} fontWeight="semibold" color="gray.900">
            {typeNamePlural}
          </Text>
          <Text fontSize={14} color="gray.600">
            {`Hier kannst du deine ${typeNamePlural} verwalten`}
          </Text>
        </VStack>
        <ShowDeletedToggle
          showDeleted={showDeleted}
          setShowDeleted={setShowDeleted}
          hasDeletedItems={hasDeletedItems}
        />
      </HStack>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={async (values) => {
          if (isValid(values)) {
            const taxRateValues = values.taxRates?.map((tr) => ({
              ...tr,
              accountingSystemId: accountingSystemTaxes
                ?.find((ast) => ast.code === tr.code)
                ?.id.toString(),
            }));

            const toAdd: CreateTaxRateInput[] | undefined = taxRateValues
              ?.filter(
                (tr) =>
                  !selectedBookkeepingFull?.taxRates?.find((i) => i.id === tr.id)
              )
              .map((tr) => {
                const index = taxRateValues?.findIndex((i) => i.id === tr.id);
                const firstItem = selectedBookkeepingFull?.taxRates?.[0]!;
                return {
                  id: tr.id,
                  code: tr.code,
                  rate: tr.rate,
                  dateFrom: tr.dateFrom.toISOString().split("T")[0],
                  dateTo: tr.dateTo
                    ? tr.dateTo.toISOString().split("T")[0]
                    : null,
                  taxType: tr.taxType,
                  accountingSystemId: tr.accountingSystemId,
                  bookkeepingId: selectedBookkeepingFull!.id,
                  sortOrder: index! + 1,
                  authGroupsRead: firstItem.authGroupsRead,
                  authGroupsUpdate: firstItem.authGroupsUpdate,
                };
              });

            const toRemove: TaxRate[] | undefined =
              selectedBookkeepingFull?.taxRates?.filter(
                (tr) => !taxRateValues?.find((i) => i.id === tr.id)
              );

            // Check if any field has changed and only add this field
            // also check if sortOrder has changed
            const toUpdate: UpdateTaxRateInput[] | undefined = taxRateValues
              ?.filter((tr, index) =>
                selectedBookkeepingFull?.taxRates?.find(
                  (i) =>
                    i.id === tr.id &&
                    (!equal(
                      {
                        code: i.code,
                        rate: i.rate,
                        dateFrom: i.dateFrom,
                        dateTo: i.dateTo,
                        taxType: i.taxType,
                        accountingSystemId: i.accountingSystemId,
                      },
                      {
                        code: tr.code,
                        rate: tr.rate,
                        dateFrom: tr.dateFrom.toISOString().split("T")[0],
                        dateTo: tr.dateTo
                          ? tr.dateTo.toISOString().split("T")[0]
                          : null,
                        taxType: tr.taxType,
                        accountingSystemId: tr.accountingSystemId,
                      }
                    ) ||
                      i.sortOrder !== index + 1)
                )
              )
              .map((tr) => {
                const initial = selectedBookkeepingFull?.taxRates?.find(
                  (i) => i.id === tr.id
                );
                const index = taxRateValues?.findIndex((i) => i.id === tr.id);
                const sortOrder = index! + 1;
                const dateFromStr = tr.dateFrom.toISOString().split("T")[0];
                const dateToStr = tr.dateTo
                  ? tr.dateTo.toISOString().split("T")[0]
                  : null;
                return {
                  id: tr.id,
                  ...(tr.code !== initial?.code ? { code: tr.code } : {}),
                  ...(tr.rate !== initial?.rate ? { rate: tr.rate } : {}),
                  ...(dateFromStr !== initial?.dateFrom
                    ? { dateFrom: dateFromStr }
                    : {}),
                  ...(dateToStr !== initial?.dateTo
                    ? { dateTo: dateToStr }
                    : {}),
                  ...(tr.taxType !== initial?.taxType
                    ? { taxType: tr.taxType }
                    : {}),
                  ...(tr.accountingSystemId !== initial?.accountingSystemId
                    ? { accountingSystemId: tr.accountingSystemId }
                    : {}),
                  ...(initial?.sortOrder !== sortOrder ? { sortOrder } : {}),
                };
              });

            // Execute the mutations in parallel
            const toAddPromises = toAdd?.map((tr) =>
              API.graphql<GraphQLQuery<CreateTaxRateInput>>(
                graphqlOperation(createTaxRate, {
                  input: tr,
                })
              )
            );
            const toRemovePromises = toRemove?.map((tr) =>
              deleteItem(updateTaxRate, tr.id)
            );
            const toUpdatePromises = toUpdate?.map((tr) =>
              API.graphql(
                graphqlOperation(updateTaxRate, {
                  input: tr,
                })
              )
            );

            await Promise.all([
              ...(toAddPromises ?? []),
              ...(toRemovePromises ?? []),
              ...(toUpdatePromises ?? []),
            ]);

            toast({
              title: "Steuersätze gespeichert",
              status: "success",
            });
          }
        }}
      >
        {({ values, isSubmitting }) => (
          <Form
            style={{
              width: "100%",
              height: "100%",
            }}
          >
            <VStack width="full" height="full" justify="space-between">
              <FieldArray name="taxRates">
                {({ remove, replace, push, move }) => (
                  <TaxRatesTable
                    accountingSystem={selectedBookkeepingFull?.accountingSystem}
                    typeNamePlural={typeNamePlural}
                    taxRates={values.taxRates}
                    hasAccountingSystemTaxes={hasAccountingSystemTaxes}
                    accountingSystemTaxes={accountingSystemTaxes}
                    remove={remove}
                    replace={replace}
                    push={push}
                    move={move}
                  />
                )}
              </FieldArray>
              <Divider />
              <HStack width="full" justify="end">
                <Button
                  variant="outline"
                  onClick={() => {
                    navigate("/");
                  }}
                  isDisabled={isSubmitting}
                >
                  Abbrechen
                </Button>
                <Button
                  colorScheme="primary"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Speichern
                </Button>
              </HStack>
            </VStack>
          </Form>
        )}
      </Formik>
    </VStack>
  );
};

export default TaxRates;
